import { Component, OnInit, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { TrackingService } from 'src/app/core/services/tracking/tracking.service';
import { version } from '../../package.json';
import { EnvironmentService } from './core/environment.service';
import { LocaleService } from './core/services';
import { FooterService } from './core/services/footer.service';
import { WINDOW } from './shared/providers/window.provider';
import { FALLBACK_LANGUAGE, getCurrentPhraseLanguage } from '@app/shared/utils';
import { CookieSettingsButtonComponent } from './shared/components/cookie-settings-button/cookie-settings-button.component';
import { RcFooterComponent } from './shared/components/rc-footer/rc-footer.component';
import { RouterOutlet } from '@angular/router';

import { MessageComponent } from '@app/shared/components/message/message.component';
import { ClientFeatureService } from './core/services/client-feature.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [
    RouterOutlet,
    RcFooterComponent,
    CookieSettingsButtonComponent,
    MessageComponent,
  ],
})
export class AppComponent implements OnInit {
  private window = inject<Window>(WINDOW);
  private footerService = inject(FooterService);
  private localeService = inject(LocaleService);
  private translateService = inject(TranslateService);
  private titleService = inject(Title);
  private trackingService = inject(TrackingService);
  private clientFeatureService = inject(ClientFeatureService);

  env = inject(EnvironmentService);

  version = version;
  isLanguageLoaded = false;
  showCookiesButton = false;

  private phraseLanguage: string = FALLBACK_LANGUAGE;

  ngOnInit(): void {
    this.titleService.setTitle(this.env.getAppTitle());
    this.setupLocales();
    this.loadServices();
  }

  setupLocales(): void {
    const configuredLanguage =
      this.clientFeatureService.clientFeatures.languagesCombinations;
    const language =
      configuredLanguage || this.window.navigator.language || FALLBACK_LANGUAGE;
    this.phraseLanguage = getCurrentPhraseLanguage(language);

    this.localeService.setLanguage(language);

    this.window.document.documentElement.setAttribute(
      'lang',
      this.phraseLanguage.split('-')[0],
    );
    this.translateService.setDefaultLang(this.phraseLanguage);
    this.translateService.use(this.phraseLanguage);

    this.translateService
      .get('registerApp_titleWelcome')
      .pipe(take(1))
      .subscribe(() => {
        this.isLanguageLoaded = true;
      });
  }

  loadServices(): void {
    const language = this.localeService.getLanguage();
    this.footerService.loadFooters(language);
    // Note: `loadOneTrust()` MUST be called after setting the document lang attribute.
    // Because the One Trust popup is translated according to the `lang` attribute of the `<html>` tag.
    this.showCookiesButton =
      !this.clientFeatureService.clientFeatures.excludeCookieBanner;
    if (this.showCookiesButton) {
      this.trackingService.loadOneTrust();
    }
    this.trackingService.loadGoogleTagManager(this.phraseLanguage);
  }
}
